import { SyntheticEvent, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Field, useField } from 'formik';
import { Autocomplete, AutocompleteRenderInputParams } from 'formik-mui';
import TextField from '@mui/material/TextField';
import { IabTaxonomyService, TaxonomyCategory } from '../../../../../api/services/iab.service';
import styled from '@emotion/styled';
import { FieldHookConfig } from 'formik/dist/Field';
import { Button } from '@mui/material';

export const ExcludesCard = (
  props: { selected: string[]; setCustomDirtyState: () => void } & FieldHookConfig<
    TaxonomyCategory[]
  >
) => {
  const [taxonomy, setTaxonomy] = useState<TaxonomyCategory[]>([]);
  const [selected, setSelected] = useState<string[]>(props.selected);
  const [field, _, helper] = useField(props);

  /**
   * Fetch the sensitive content taxonomy
   */
  useEffect(() => {
    IabTaxonomyService.fetchSensitiveContent().then((categories) => {
      // used for options in autocomplete component
      setTaxonomy(categories);
    });
  }, []);

  // Update state when props change
  useEffect(() => {
    setSelected(props.selected);
  }, [props]);

  /**
   * Used for setting the selected values from props
   * This would not be needed it the AutoComplete component would be using the ids instead of objects:
   * https://github.com/mui/material-ui/issues/23708
   * https://github.com/mui/material-ui/issues/37287
   */
  useEffect(() => {
    // reactive dependencies ftw.
    if (taxonomy.length === 0) {
      return;
    }
    
    let mapping = new Map(taxonomy.map((cat) => [cat.id, cat]));
    
    // If no selected categories provided, select all by default
    if (selected.length === 0) {
      console.log('No categories selected, selecting all by default');
      helper.setValue(taxonomy, false).then();
    } else {
      // Set the already selected values
      console.log(`Selected sensitive topics: ${selected}`);
      helper
        .setValue(
          selected.map((id) => mapping.get(id)!),
          false
        )
        .then();
    }
  }, [taxonomy, selected]);

  const handleSelectAll = () => {
    props.setCustomDirtyState();
    helper.setValue(taxonomy, true).catch();
  };

  const handleClearAll = () => {
    props.setCustomDirtyState();
    helper.setValue([], true).catch();
  };

  // Select All should be disabled when all categories are selected
  const isSelectAllDisabled = taxonomy.length === 0 || field.value?.length === taxonomy.length;

  return (
    <Wrapper>
      <ButtonContainer>
        <Button size="small" onClick={handleSelectAll} disabled={isSelectAllDisabled}>
          Select All
        </Button>
        <Button size="small" onClick={handleClearAll} disabled={field.value?.length === 0}>
          Clear All
        </Button>
      </ButtonContainer>
      <Field
        className="buyer-pro-autocomplete category-autocomplete"
        {...field}
        multiple
        component={Autocomplete}
        options={taxonomy}
        getOptionLabel={(category: TaxonomyCategory) => category.name}
        onChange={(_: SyntheticEvent, newValue: TaxonomyCategory[]) => {
          props.setCustomDirtyState();
          return helper.setValue(newValue, true).catch();
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            label={'Sensitive Categories'}
            placeholder="Optional"
            variant="outlined"
            {...params}
          />
        )}
      />
    </Wrapper>
  );
};

const CardTitle = styled.h3`
  margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;
